'use client'
import Link from 'next/link'
import React, { memo, useMemo } from 'react'

import { cn } from '@/utils/twMerge'
import { PrismicText } from '@prismicio/react'
import { get, orderBy } from '@/utils/lodashReplacements'
import { getCMMCLinkType } from '@/utils/getCMMCLinkType'
import { HubSidebarProps, HubDocument } from '@/types/hub'
import type { RichTextField } from '@/types/prismic'
import { HUB_SECTION_SCROLL_OFFSET_PX } from '@/utils/smoothScroll'
import { FRAMEWORKS_WITH_CUSTOM_URLS } from '@/constants/hubFrameworks'
import { hubSidebarTitleHasVisibleContent, richTextFieldHasVisibleText } from '@/utils/prismicUtilityFunction'
import { useScrollspyWithManualControl } from '@/hooks/useScrollspyWithManualControl'

import { useLocale } from '@/context/LocaleContext'

interface HubSection {
  slice_type?: string
  primary?: {
    section_id?: string
    section_header?: RichTextField | null
  }
}

type HubSectionLink = {
  id: string
  header: RichTextField
}

function getHubSectionLinks(item: HubDocument | undefined): HubSectionLink[] {
  if (!item) return []

  const itemData = item.data as { body?: HubSection[]; slices?: HubSection[] }
  const bodyContent = itemData.body ?? itemData.slices
  if (!bodyContent) return []

  const links: HubSectionLink[] = []
  for (const section of bodyContent) {
    const sectionId = section.primary?.section_id
    const header = section.primary?.section_header
    if (
      section.slice_type !== 'DetailsSection' ||
      !sectionId ||
      !header ||
      !richTextFieldHasVisibleText(header)
    ) {
      continue
    }
    links.push({ id: sectionId, header })
  }
  return links
}

const Sidebar: React.FC<HubSidebarProps> = memo(({ uid, title, resources, slug = uid, linkType }) => {
  const locale = useLocale()
  const sortedItems = useMemo(
    () => orderBy(get(resources, uid), 'data.article_position', 'asc') as HubDocument[],
    [resources, uid],
  )

  const currentSectionLinks = useMemo(() => {
    const currentArticle = sortedItems.find((item) => item.uid === slug)
    return getHubSectionLinks(currentArticle)
  }, [sortedItems, slug])

  const sectionIds = useMemo(() => currentSectionLinks.map((section) => section.id), [currentSectionLinks])

  const { activeItemId, handleItemClick } = useScrollspyWithManualControl({
    items: sectionIds,
    offset: HUB_SECTION_SCROLL_OFFSET_PX,
  })

  const renderTitle = () => (typeof title === 'string' ? title : <PrismicText field={title} />)

  const hasSidebarTitle = hubSidebarTitleHasVisibleContent(title)

  const sidebarHeadingId = 'hub-article-sidebar-title'

  return (
    <aside
      className="sticky top-[90px] hidden rounded-md border border-secondary-gray-100 px-4 py-6 shadow-light backdrop-blur-25 lg:inline-block"
      aria-labelledby={hasSidebarTitle ? sidebarHeadingId : undefined}
      aria-label={hasSidebarTitle ? undefined : 'Articles in this chapter'}>
      {hasSidebarTitle ? (
        <span id={sidebarHeadingId} className="mb-4 inline-block text-lg font-medium">
          {renderTitle()}
        </span>
      ) : null}
      <ul>
        {sortedItems.map((item, index) => (
          <li
            key={`item-${item.uid}-${index}`}
            className={cn(
              '[&:not(:last-child)]:mb-2',
              item.uid === slug
                ? '[&>a]:bg-primary-green-85 [&>a]:text-primary-ink-100 [&>a]:transition [&>a]:duration-500'
                : '',
            )}>
            <Link
              href={
                FRAMEWORKS_WITH_CUSTOM_URLS.includes(linkType as string)
                  ? getCMMCLinkType(item.uid, linkType as string, locale)
                  : item.url
              }
              aria-current={item.uid === slug ? 'page' : undefined}
              className="block rounded-md py-2 px-3 text-primary-ink-60 transition-colors duration-200 hover:bg-primary-green-85 hover:text-primary-ink-100">
              <PrismicText field={(item.data as any).title} />
            </Link>
            {item.uid === slug && currentSectionLinks.length > 0 ? (
              <nav aria-label="On this page">
                <ul className="pl-2">
                  {currentSectionLinks.map((section) => {
                    const isActive = activeItemId === section.id
                    return (
                      <li key={section.id} className="mb-0">
                        <a
                          href={`#${section.id}`}
                          aria-current={isActive ? true : undefined}
                          onClick={(event) => {
                            event.preventDefault()
                            handleItemClick(section.id)
                          }}
                          className={cn(
                            'block w-full rounded-md py-3 pl-3 pr-0 text-left outline-none transition-[color,background-color,transform] duration-200 focus-visible:ring-2 focus-visible:ring-primary-green-85 focus-visible:ring-offset-2',
                            isActive
                              ? 'bg-primary-green-20 font-medium text-primary-ink-100'
                              : 'bg-transparent text-primary-ink-60 hover:translate-x-1 hover:text-primary-ink-100',
                          )}>
                          <PrismicText field={section.header} />
                        </a>
                      </li>
                    )
                  })}
                </ul>
              </nav>
            ) : null}
          </li>
        ))}
      </ul>
    </aside>
  )
})

Sidebar.displayName = 'Sidebar'

export default Sidebar
